import type { FC, ReactNode } from 'react'
import type { WashExtra } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { getExtraIcon } from '@/lib/extra-icons'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'

interface ExtrasSectionProps {
    extras: Array<WashExtra>
}

export const ExtrasSection: FC<ExtrasSectionProps> = ({ extras }): ReactNode => {
    if (extras.length === 0) return null

    return (
        <section id='add-ons' aria-labelledby='add-ons-title' className='scroll-mt-20 bg-foam py-20 sm:py-28'>
            <Container>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        id='add-ons-title'
                        eyebrow='Add-ons'
                        title='Finish it your way'
                        description='Add any of these to a package when you book online, or just ask when you arrive.'
                    />

                    <Link
                        href='/book'
                        className='inline-flex shrink-0 items-center gap-2 self-start rounded-full font-semibold text-aqua-deep transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring lg:self-auto'
                    >
                        Add extras when you book
                        <ArrowRight className='size-4' />
                    </Link>
                </div>

                <ul className='mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                    {extras.map(extra => {
                        const Icon = getExtraIcon(extra.name)

                        return (
                            <li key={extra.id} className='flex gap-4 rounded-2xl bg-card p-5 shadow-sm ring-1 ring-ink/5 transition-shadow hover:shadow-lg hover:shadow-ink/5'>
                                <span className='grid size-12 shrink-0 place-items-center rounded-xl bg-foam text-aqua-deep'>
                                    <Icon className='size-5' />
                                </span>

                                <div className='min-w-0 flex-1'>
                                    <div className='flex items-baseline justify-between gap-3'>
                                        <h3 className='text-lg font-semibold leading-snug text-ink' data-mdlfy-cms={`extras:${extra.id}:name`}>
                                            {extra.name}
                                        </h3>

                                        {extra.price && (
                                            <span className='shrink-0 font-heading text-2xl font-bold leading-none text-ink' data-mdlfy-cms={`extras:${extra.id}:price`}>
                                                {extra.price}
                                            </span>
                                        )}
                                    </div>

                                    {extra.description && (
                                        <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={`extras:${extra.id}:description`}>
                                            {extra.description}
                                        </p>
                                    )}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </Container>
        </section>
    )
}
